import React, { ReactElement, useContext, useEffect } from "react";
import {
	ClientUserContactType,
	CommonServerStatusEnum,
	ListPagePostRequest,
	RelatedClientInformation,
	TableQueryFilter,
	VendorOpportunity,
	ZomentumEntities
} from "@/Contracts";
// import AddOpportunityDrawer from "@/Components/OpportunitiesPage/Drawers/AddOpportunityDrawer";
// import AddClientDrawer from "../../Clients/Client/Add";
// import EmailThreadMessageDrawer from "@/Components/Others/EmailThread/Drawers/EmailThreadMessageDrawer";
import ZDrawer from "@molecules/ZDrawer";
import {
	Opportunity,
	OpportunityRequest,
	OpportunityType
} from "@/Contracts/Opportunity";
import {
	ClientContactBulkUpdate,
	ClientContactBulkUpdateRequest,
	ClientsRequest
} from "@/Contracts/Clients";
import {
	ESalesActivityType,
	RelatedOpportunityInformation
} from "@/Contracts/Others/SalesActivity";
import { ChildEntityHydrationParams } from "@/Contracts/Utils";
import { getTaskContact } from "../../../../utils";
import {
	ComponentType,
	FormUIPropsType,
	IAddSalesActivityWrapperProps
} from "../interface";
import useDrawersCommonFunctions from "../../../../hooks/SalesActivity/useSalesActivityDrawersCommonFunctions";
import {
	IOnlyCommentUIProps,
	IOnlyLogCallUIProps,
	IOnlyLogEmailUIProps,
	IOnlyMeetingUIProps,
	IOnlyTaskUIProps
} from "../../../../templates/ZDrawers/ZSalesActivities/interface";
import { MissingDetailsModal } from "@organisms/ZMissingDetailsModal";
import ZMessage from "@molecules/ZMessage";
import {
	SalesActivityModuleState,
	useSalesActivityModuleSelector
} from "../../../../store";
import useDatePattern from "@/Hooks/useDatePattern";
import { getOpportunityDetails } from "../../../../services/Opportunity";
import { ParentContext } from "../../../../store/context/ParentContext";
import { isVendorApp } from "@/Utils";
import { getVendorOpportunityDetails } from "../../../../services/Vendor/Opportunity";
import "./index.less";

const EditWapper = <T1 extends ComponentType, T2 extends FormUIPropsType>({
	visible = false,
	onClose,
	Component,
	form,
	formUIProps,
	salesActivityType
}: IAddSalesActivityWrapperProps<T1, T2>): ReactElement => {
	const parentContext = useContext(ParentContext);
	const { isLoading, currentActiveSalesActivity } =
		useSalesActivityModuleSelector((state: SalesActivityModuleState) => {
			return {
				isLoading: state.salesActivity.isLoading,
				currentActiveSalesActivity:
					state.salesActivity.currentActiveSalesActivity
			};
		});
	const { isLoading: isUserLoading } = parentContext.user;
	const { currentActiveOpportunity, addOpportunityDrawerOpen } =
		parentContext.opportunity;
	const { addClientDrawerOpen } = parentContext.client;

	const datePattern = useDatePattern();

	const uiProps =
		salesActivityType === ESalesActivityType.MEETING
			? (formUIProps as IOnlyMeetingUIProps)
			: null;
	const isSystemEmail =
		salesActivityType === ESalesActivityType.LOGEMAIL &&
		!!(formUIProps as IOnlyLogEmailUIProps)?.systemEmail;
	const formItemsProps = {
		opportunitySelect: { disabled: isSystemEmail },
		clientSelect: { disabled: isSystemEmail },
		userSelect: { disabled: isSystemEmail }
	};
	const {
		setOpportunitySearchResult,
		setClientSearchResult,
		setClientContactsSearchResult,
		setVendorOpportunitySearchResult,
		setTaskContact,
		setPartnerSearchResult,
		setVendorClientSearchResult,
		taskContact,
		clientContactsSearchResult,
		updateTaskContact,
		emailThreadMessageDrawerOpen,
		getEmailThreadMessage,
		showEmailThreadMessageDrawer,
		closeEmailThreadMessageDrawer,
		closeAddOpportunityDrawer,
		closeAddClientDrawer,
		fetchOpportunities,
		fetchClients,
		fetchClientContacts,
		ZUserMultiSelectFormItem,
		ZOpportunitySelectFormItem,
		ZClientSelectFormItem,
		ZUserSelectFormItem,
		ZVendorPartnerSelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZVendorClientSelectFormItem,
		selectedClient
	} = useDrawersCommonFunctions(
		form,
		salesActivityType,
		[
			ESalesActivityType.MEETING,
			ESalesActivityType.TASK,
			ESalesActivityType.LOGCALL,
			ESalesActivityType.COMMENT
		].includes(salesActivityType)
			? (
					formUIProps as IOnlyMeetingUIProps &
						IOnlyTaskUIProps &
						IOnlyLogCallUIProps &
						IOnlyCommentUIProps
			  ).handleOwnerChange
			: null,
		null,
		formItemsProps
	);

	useEffect(() => {
		const fetchDataForPartnerApp = async () => {
			if (visible) {
				const feedDetails = currentActiveSalesActivity?.feed_details;
				form.setFieldsValue({
					relation: feedDetails?.entity_type
				});
				let initialOpportunityId: string | null = null;
				let initialClientId: string | null = null;
				const opportunityId =
					feedDetails?.entity_type === ZomentumEntities.Opportunity
						? feedDetails?.entity_id
						: undefined;
				let opportunityDetail = currentActiveOpportunity as Opportunity;
				if (
					opportunityId &&
					currentActiveOpportunity?.id !== opportunityId
				) {
					opportunityDetail = await getOpportunityDetails(
						opportunityId
					);
				}
				const clientId =
					feedDetails?.entity_type === ZomentumEntities.Client
						? feedDetails?.entity_id
						: opportunityDetail?.client_company_id ??
						  (
								feedDetails?.entity_obj as RelatedOpportunityInformation
						  )?.client?.id;

				const opportunities = await fetchOpportunities(
					new OpportunityRequest(
						new Array<TableQueryFilter>(),
						null,
						[],
						50,
						opportunityId
					)
				);
				const clients = await fetchClients(
					new ClientsRequest(
						new Array<TableQueryFilter>(),
						null,
						50,
						new Array<ChildEntityHydrationParams>(
							ChildEntityHydrationParams.CLIENT_USERS_COUNT
						),
						undefined,
						clientId
					)
				);
				if (feedDetails) {
					if (
						feedDetails.entity_type === ZomentumEntities.Opportunity
					) {
						initialOpportunityId = feedDetails.entity_id ?? null;
						initialClientId =
							!opportunityDetail?.is_onboarding_entity
								? opportunityDetail?.client_company_id ??
								  (
										feedDetails.entity_obj as RelatedOpportunityInformation
								  )?.client.id ??
								  null
								: null;
					} else if (
						feedDetails.entity_type === ZomentumEntities.Client
					) {
						initialOpportunityId = null;
						initialClientId = feedDetails.entity_id;
					}
					form.setFieldsValue({
						[`${ZomentumEntities.Opportunity}`]:
							initialOpportunityId,
						[`${ZomentumEntities.Client}`]: initialClientId
					});
					setOpportunitySearchResult(opportunities);
					setClientSearchResult(clients);
					if (
						initialClientId &&
						(salesActivityType === ESalesActivityType.TASK ||
							salesActivityType === ESalesActivityType.MEETING)
					) {
						const contacts = await fetchClientContacts(
							initialClientId
						);
						setTaskContact(
							getTaskContact(
								feedDetails.entity_type,
								feedDetails.entity_type ===
									ZomentumEntities.Opportunity
									? initialOpportunityId
									: initialClientId,
								contacts,
								opportunities,
								clients
							)
						);
						setClientContactsSearchResult(contacts);
					}
				}
			} else {
				setClientSearchResult(null);
				setOpportunitySearchResult(null);
				setClientContactsSearchResult(null);
				setTaskContact(null);
			}
		};

		const fetchDataForVendorApp = async () => {
			if (visible) {
				const feedDetails = currentActiveSalesActivity?.feed_details;
				form.setFieldsValue({
					relation: feedDetails?.entity_type
				});
				let initialOpportunityId: string | null = null;
				let initialClientId: string | null = null;
				let initialPartnerId: string | null = null;

				const opportunityId =
					feedDetails?.entity_type ===
					ZomentumEntities.VendorOpportunity
						? feedDetails?.entity_id
						: undefined;
				let opportunityDetail =
					currentActiveOpportunity as VendorOpportunity;
				if (
					opportunityId &&
					currentActiveOpportunity?.id !== opportunityId
				) {
					opportunityDetail = await getVendorOpportunityDetails(
						opportunityId
					);
				}

				const partnerId =
					feedDetails?.entity_type === ZomentumEntities.VendorCompany
						? feedDetails?.entity_id
						: undefined;
				const clientId =
					feedDetails?.entity_type === ZomentumEntities.VendorClient
						? feedDetails?.entity_id
						: undefined;

				const partnerResponse =
					await parentContext.vendorPartner.getPartnerListPagePost(
						new ListPagePostRequest({
							count_query_flag: true,
							filter_criterion: [],
							sort_criterion: [],
							page_no: 1,
							page_size: 50,
							includedChildEntities: [
								ChildEntityHydrationParams.USERS_ALL
							]
						})
					);
				const clientResponse =
					await parentContext.vendorClient.getClientListPagePost(
						new ListPagePostRequest({
							count_query_flag: true,
							filter_criterion: [],
							sort_criterion: [],
							page_no: 1,
							page_size: 50,
							includedChildEntities: [
								ChildEntityHydrationParams.USERS_ALL
							]
						})
					);
				const opportunityResponse =
					await parentContext.opportunity.getOpportunityListPost(
						new ListPagePostRequest({
							count_query_flag: true,
							filter_criterion: [],
							sort_criterion: [],
							page_no: 1,
							page_size: 50,
							includedChildEntities: [
								ChildEntityHydrationParams.USERS_ALL
							]
						})
					);
				if (feedDetails) {
					if (
						feedDetails.entity_type ===
						ZomentumEntities.VendorOpportunity
					) {
						initialOpportunityId = opportunityId ?? null;
						initialClientId =
							opportunityDetail?.vendor_client_company_id ?? null;
					} else if (
						feedDetails.entity_type ===
						ZomentumEntities.VendorClient
					) {
						initialOpportunityId = null;
						initialPartnerId = null;
						initialClientId = clientId ?? null;
					} else if (
						feedDetails.entity_type ===
						ZomentumEntities.VendorPartner
					) {
						initialOpportunityId = null;
						initialClientId = null;
						initialPartnerId = partnerId ?? null;
					}
					form.setFieldsValue({
						[`${ZomentumEntities.VendorOpportunity}`]:
							initialOpportunityId,
						[`${ZomentumEntities.VendorClient}`]: initialClientId,
						[`${ZomentumEntities.VendorCompany}`]: initialPartnerId
					});
					setPartnerSearchResult(partnerResponse.data);
					setVendorClientSearchResult(clientResponse.data);
					setVendorOpportunitySearchResult(opportunityResponse.data);
				}
				if (
					(initialClientId &&
						salesActivityType === ESalesActivityType.TASK) ||
					salesActivityType === ESalesActivityType.MEETING
				) {
					const contacts = await fetchClientContacts(initialClientId);
					setClientContactsSearchResult(contacts);
				}
				return;
			} else {
				setPartnerSearchResult(null);
				setVendorClientSearchResult(null);
				setClientContactsSearchResult(null);
				setVendorOpportunitySearchResult(null);
				setTaskContact(null);
			}
		};

		isVendorApp() ? fetchDataForVendorApp() : fetchDataForPartnerApp();
	}, [visible, currentActiveSalesActivity]);

	const opportunityDrawerOnSubmit = async () => {
		const opportunityId =
			currentActiveSalesActivity?.feed_details?.entity_type ===
			ZomentumEntities.Opportunity
				? currentActiveSalesActivity.feed_details.entity_id
				: undefined;
		const opportunities = await fetchOpportunities(
			new OpportunityRequest(
				new Array<TableQueryFilter>(),
				null,
				new Array<ChildEntityHydrationParams>(),
				100,
				opportunityId
			)
		);
		setOpportunitySearchResult(opportunities);
	};

	const clientDrawerOnSubmit = async () => {
		const clientId =
			currentActiveSalesActivity?.feed_details?.entity_type ===
			ZomentumEntities.Client
				? currentActiveSalesActivity.feed_details.entity_id
				: undefined;
		const clients = await fetchClients(
			new ClientsRequest(
				new Array<TableQueryFilter>(),
				null,
				100,
				new Array<ChildEntityHydrationParams>(
					ChildEntityHydrationParams.CLIENT_USERS_COUNT
				),
				undefined,
				clientId
			)
		);
		setClientSearchResult(clients);
	};

	const clientContactBulkUpdateSubmitHandler = async (
		values: ClientContactBulkUpdate[]
	) => {
		const payload: ClientContactBulkUpdateRequest = { data: [...values] };
		try {
			const { results } =
				await parentContext.client.bulkUpdateClientContactEmail(
					payload
				);

			results.forEach(value => {
				if (value.status === CommonServerStatusEnum.Failure) {
					ZMessage.error(value.response);
					return;
				}
			});
			const clientId = form.getFieldValue(ZomentumEntities.Client);
			if (!clientId) return;

			const clientContacts = await fetchClientContacts(clientId);
			setClientContactsSearchResult(clientContacts);
			uiProps && uiProps.setAttendeesWithoutEmail([]);
		} catch (err) {
			console.error(err);
		}
		uiProps && uiProps.setShowAddMissingDetailsModal(false);
		form.submit();
	};

	const emailThreadMessage =
		salesActivityType === ESalesActivityType.TASK
			? getEmailThreadMessage()
			: null;

	const openAddClientContactDrawer = () => {
		parentContext.client.addClientContactDrawerOpened();
	};

	const UIProps: unknown = {
		onClose,
		form,
		isLoading,
		taskContact,
		clientContactsSearchResult,
		showEmailThreadMessageDrawer,
		ZUserMultiSelectFormItem,
		updateTaskContact,
		ZOpportunitySelectFormItem,
		ZClientSelectFormItem,
		ZUserSelectFormItem,
		ZVendorPartnerSelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZVendorClientSelectFormItem,
		isSubmitDisabled: isLoading || isUserLoading,
		datePattern,
		openAddClientContactDrawer,
		...formUIProps
	};

	const TypedComponent = Component as React.FC<T1>;
	const typedUIProps = UIProps as T1;

	const handleClose = () => {
		onClose && onClose();
	};

	const currentActiveClient =
		currentActiveSalesActivity?.feed_details.entity_type ===
		ZomentumEntities.Client
			? currentActiveSalesActivity?.feed_details?.entity_obj
			: null;

	const currentActiveClientId =
		selectedClient ??
		currentActiveClient?.id ??
		(isVendorApp()
			? (currentActiveOpportunity as VendorOpportunity)
					?.vendor_client_company_id
			: (currentActiveOpportunity as Opportunity)?.client_company_id) ??
		currentActiveSalesActivity?.feed_details?.entity_id;

	const editContactType =
		(currentActiveClient as RelatedClientInformation)?.client_users?.data
			?.length ||
		!!currentActiveOpportunity?.primary_client_user_id?.length ||
		!!clientContactsSearchResult?.length
			? ClientUserContactType.SecondaryContact
			: ClientUserContactType.PrimaryContact;

	const isViewMode = (() => {
		if (salesActivityType === ESalesActivityType.COMMENT)
			return !!(formUIProps as IOnlyCommentUIProps).isReadOnly;
		else if (salesActivityType === ESalesActivityType.TASK)
			return !!(formUIProps as IOnlyTaskUIProps).isReadOnly;
		else if (salesActivityType === ESalesActivityType.LOGCALL)
			return !!(formUIProps as IOnlyLogCallUIProps).isReadOnly;
		else return false;
	})();

	return (
		<>
			<ZDrawer
				title={`${isViewMode ? "View" : "Edit"} ${salesActivityType}`}
				removeDefaultPadding
				visible={visible}
				closable
				destroyOnClose
				onClose={handleClose}
				className={isViewMode ? "read-only" : ""}
			>
				<TypedComponent {...typedUIProps} />

				<parentContext.opportunity.AddOpportunityDrawer
					visible={addOpportunityDrawerOpen}
					onClose={closeAddOpportunityDrawer}
					activeClientUserId={null}
					activeOpportunityType={
						OpportunityType.ApplicationDevelopment
					}
					activeClientCompanyId={null}
					onSubmit={opportunityDrawerOnSubmit}
				/>

				<parentContext.client.AddClientDrawer
					visible={addClientDrawerOpen}
					onClose={closeAddClientDrawer}
					onSubmit={clientDrawerOnSubmit}
				/>

				{isVendorApp() ? (
					<parentContext.client.AddClientContactDrawer
						visible={
							parentContext.client.addClientContactDrawerOpen
						}
						currentActiveVendorClientContact={null}
						onClose={
							parentContext.client.addClientContactDrawerClosed
						}
						handleClientContactRefresh={clientContact => {
							setClientContactsSearchResult(contacts => [
								...contacts,
								clientContact
							]);
						}}
						initalVendorClientContact={false}
						selectedClient={currentActiveClientId}
					/>
				) : (
					<parentContext.client.AddClientContactDrawer
						visible={
							parentContext.client.addClientContactDrawerOpen
						}
						clientId={currentActiveClientId}
						activeTabContactType={editContactType}
						onSubmit={clientContact => {
							setClientContactsSearchResult(contacts => [
								...contacts,
								clientContact
							]);
						}}
						onClose={
							parentContext.client.addClientContactDrawerClosed
						}
					/>
				)}
				{isVendorApp() ? (
					<parentContext.emailThread.EmailThreadMessageDrawer
						visible={emailThreadMessageDrawerOpen}
						onClose={closeEmailThreadMessageDrawer}
						isNewThread
						isDocumentMessage={false}
						partnerId={form.getFieldValue(ZomentumEntities.Client)}
						opportunityId={form.getFieldValue(
							ZomentumEntities.Opportunity
						)}
						useTemplate={false}
						isFromDocumentsPage={false}
						defaultRecipients={[taskContact?.email]}
						emailThreadMessage={emailThreadMessage}
					/>
				) : (
					<parentContext.emailThread.EmailThreadMessageDrawer
						visible={emailThreadMessageDrawerOpen}
						onClose={closeEmailThreadMessageDrawer}
						isNewThread
						isDocumentMessage={false}
						clientId={form.getFieldValue(ZomentumEntities.Client)}
						opportunityId={form.getFieldValue(
							ZomentumEntities.Opportunity
						)}
						documentId={null}
						useTemplate={false}
						isFromDocumentsPage={false}
						defaultRecipients={[taskContact?.email]}
						emailThreadMessage={emailThreadMessage}
					/>
				)}

				{uiProps && (
					<MissingDetailsModal
						title="Missing Attendees’ Email"
						visible={uiProps.showAddMissingDetailsModal}
						onSubmit={clientContactBulkUpdateSubmitHandler}
						onCancel={() =>
							uiProps.setShowAddMissingDetailsModal(false)
						}
						missingEmailKeys={uiProps.attendeesWithoutEmail}
						subtitle="For assigning Attendees to the meeting, you need to add emails for the following recipients"
					/>
				)}
			</ZDrawer>
		</>
	);
};

export default EditWapper;
